/**
 * VirtualList — a list that keeps only the rows on screen in the DOM.
 *
 * Task 05, checklist item 2: «Список из 3000 сидированных треков рендерится без тормозов:
 * элементов в DOM ограниченное число». The target is a mid-range Android inside Telegram's
 * webview, where three thousand rows of nested boxes cost the first scroll its frame budget
 * before a finger has moved. So only the rows inside the scroller (plus {@link DEFAULT_OVERSCAN}
 * on each side) exist at any moment, and `apps/web/test/tracks.test.tsx` counts them.
 *
 * Carried over from the old project with two changes: the size-query container it set for a
 * BPM column this product does not have is gone, and the offset of the list inside the
 * DOCUMENT scroller is measured correctly (the old code only ever ran inside its own
 * scroller and subtracted the page offset twice there).
 *
 * ## Padding, not absolute positioning
 *
 * The skipped rows are expressed as padding on the inner element — the height of everything
 * above the window on top, everything below it underneath. Rows stay in normal flow, so a
 * sticky header above the list keeps sticking and each row keeps its own box.
 *
 * The cost is that heights are declared, not measured: a caller gives every row the height
 * its CSS gives it. The rows of this product are fixed-height by design, and a measuring
 * list would need a `ResizeObserver` per row — exactly the per-row machinery a virtual list
 * exists to avoid.
 *
 * ## The scroller is found, not owned
 *
 * `app/Shell.tsx` owns the one scroll container of the app and hands it down through
 * {@link ScrollPortContext}. A list that opened its own `overflow: auto` would nest a second
 * scroller inside the first — two scrollbars, a broken sticky header, and Telegram's
 * swipe-to-close fighting the inner one. Without a context the nearest scrollable ancestor is
 * used, and without one of those, the document.
 *
 * There is no `requestAnimationFrame` here (`spec.md` «Плеер» bans it from anything that
 * must keep working in the background). A scroll listener that calls `setState` only when the
 * WINDOW changes is already cheap, and React batches the rest.
 */

import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';

import { ScrollPortContext } from '../app/scroll-port';

/* ══ model ═════════════════════════════════════════════════════════════════════════ */

/** One row. `height` is what the row's CSS gives it, in pixels; `key` is stable per row. */
export interface VirtualRow {
  readonly key: string;
  readonly height: number;
}

/** A half-open window `[start, end)` over the rows. */
export interface VisibleRange {
  readonly start: number;
  readonly end: number;
}

/** Rows kept above and below the scroller, so a flick never shows a gap. */
export const DEFAULT_OVERSCAN = 4;

/**
 * The scroller height to use before it can be measured.
 *
 * The first render happens before any effect, and in a test environment there is no layout
 * at all, so `clientHeight` is `0`. Windowing to zero would render nothing and then flash a
 * full window in; the window's own height is a real number, never a guess.
 */
export function fallbackViewportHeight(): number {
  const height = window.innerHeight;
  return Number.isFinite(height) && height > 0 ? height : 640;
}

/* ══ geometry ══════════════════════════════════════════════════════════════════════ */

/** Prefix sums: `offsets[i]` is the top of row `i`, the last entry the total height. */
export function rowOffsets(rows: readonly VirtualRow[]): number[] {
  const offsets: number[] = new Array<number>(rows.length + 1);
  offsets[0] = 0;
  let accumulated = 0;
  for (let index = 0; index < rows.length; index += 1) {
    const height = rows[index]?.height ?? 0;
    accumulated += Number.isFinite(height) && height > 0 ? height : 0;
    offsets[index + 1] = accumulated;
  }
  return offsets;
}

/** The index of the row containing `y`, clamped to the list. */
function indexAt(offsets: readonly number[], y: number): number {
  let low = 0;
  let high = offsets.length - 2;
  if (high <= 0) return 0;
  while (low < high) {
    const mid = (low + high + 1) >> 1;
    if ((offsets[mid] ?? 0) <= y) low = mid;
    else high = mid - 1;
  }
  return low;
}

/** The window covering `[top, bottom)` of the list, widened by `overscan` rows each way. */
export function rangeFor(
  offsets: readonly number[],
  top: number,
  bottom: number,
  overscan: number,
): VisibleRange {
  const count = Math.max(0, offsets.length - 1);
  if (count === 0) return { start: 0, end: 0 };

  const first = indexAt(offsets, Math.max(0, top));
  const last = indexAt(offsets, Math.max(0, bottom));
  const pad = Math.max(0, Math.trunc(overscan));

  return {
    start: Math.max(0, first - pad),
    end: Math.min(count, last + 1 + pad),
  };
}

function documentScroller(): HTMLElement {
  const scroller = document.scrollingElement ?? document.documentElement;
  return scroller instanceof HTMLElement ? scroller : document.documentElement;
}

/** The nearest scrollable ancestor, or the document scroller. */
function resolvePort(node: HTMLElement): HTMLElement {
  let parent: HTMLElement | null = node.parentElement;
  while (parent !== null) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return parent;
    parent = parent.parentElement;
  }
  return documentScroller();
}

/** Where scroll events for `port` are delivered: the window, for the document scroller. */
function scrollTargetFor(port: HTMLElement): EventTarget {
  return port === documentScroller() ? window : port;
}

/**
 * The list's top in the scroller's content coordinates.
 *
 * For an element scroller the scroller's own rect is fixed while its content moves, so the
 * scroll position has to be added back. For the document the element rect is already
 * relative to the viewport and the viewport IS the scroller, so the page offset is added
 * once and the scroller's rect not at all.
 */
function measureOffset(port: HTMLElement, node: HTMLElement): number {
  const top = node.getBoundingClientRect().top;
  const value =
    port === documentScroller()
      ? top + window.scrollY
      : top - port.getBoundingClientRect().top + port.scrollTop;
  return Number.isFinite(value) ? value : 0;
}

/* ══ component ═════════════════════════════════════════════════════════════════════ */

export interface VirtualListProps<T extends VirtualRow> {
  /** Every row, in order — including a footer row, if the caller has one. */
  readonly rows: readonly T[];
  /** Renders one row. Called only for rows inside the window. */
  readonly renderRow: (row: T, index: number) => ReactNode;
  readonly overscan?: number;
  /** Notified whenever the window moves, so a screen can page in more rows. */
  readonly onRangeChange?: ((range: VisibleRange) => void) | undefined;
  /** The inner element's tag. `ul` when the caller renders `li` rows. */
  readonly as?: 'div' | 'ul' | 'ol';
  readonly className?: string | undefined;
  readonly ariaLabel?: string | undefined;
}

export function VirtualList<T extends VirtualRow>({
  rows,
  renderRow,
  overscan = DEFAULT_OVERSCAN,
  onRangeChange,
  as = 'div',
  className,
  ariaLabel,
}: VirtualListProps<T>): JSX.Element {
  const contextPort = useContext(ScrollPortContext);
  const outerRef = useRef<HTMLDivElement | null>(null);
  const portRef = useRef<HTMLElement | null>(null);
  const offsetRef = useRef<number>(0);

  const offsets = useMemo(() => rowOffsets(rows), [rows]);
  const totalHeight = offsets[offsets.length - 1] ?? 0;

  const [range, setRange] = useState<VisibleRange>(() =>
    rangeFor(offsets, 0, fallbackViewportHeight(), overscan),
  );

  /** The window, from the live scroll position. */
  const recompute = useCallback((): void => {
    const port = portRef.current;
    const viewport =
      port === null || port.clientHeight <= 0 ? fallbackViewportHeight() : port.clientHeight;
    const top = (port?.scrollTop ?? 0) - offsetRef.current;
    const next = rangeFor(offsets, top, top + viewport, overscan);
    setRange((previous) =>
      previous.start === next.start && previous.end === next.end ? previous : next,
    );
  }, [offsets, overscan]);

  /*
   * Subscribe to the scroller. Re-runs when the content height changes, which is also when
   * this list's offset inside the scroller can have moved.
   */
  useEffect(() => {
    const outer = outerRef.current;
    if (outer === null) return;

    const port = contextPort ?? resolvePort(outer);
    portRef.current = port;
    offsetRef.current = measureOffset(port, outer);
    recompute();

    const handle = (): void => {
      recompute();
    };
    const target = scrollTargetFor(port);
    target.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);

    return () => {
      target.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [recompute, contextPort, totalHeight]);

  /* Clamp at render: `rows` can shrink between a scroll and the next paint. */
  const count = rows.length;
  const start = Math.min(Math.max(0, range.start), count);
  const end = Math.min(Math.max(start, range.end), count);

  useEffect(() => {
    onRangeChange?.({ start, end });
  }, [onRangeChange, start, end]);

  const padStart = offsets[start] ?? 0;
  const padEnd = Math.max(0, totalHeight - (offsets[end] ?? totalHeight));

  const Inner = as;
  const innerStyle: CSSProperties = {
    paddingBlockStart: `${String(padStart)}px`,
    paddingBlockEnd: `${String(padEnd)}px`,
  };

  return (
    <div
      ref={outerRef}
      className={className}
      data-virtual="true"
      data-rows={String(count)}
      data-window={`${String(start)}-${String(end)}`}
    >
      <Inner className="virtualInner" style={innerStyle} aria-label={ariaLabel}>
        {/* The key lives on a Fragment: the row's identity belongs to the list, and a
            Fragment adds no node to a `<ul>` that may only hold `<li>` children. */}
        {rows.slice(start, end).map((row, offset) => (
          <Fragment key={row.key}>{renderRow(row, start + offset)}</Fragment>
        ))}
      </Inner>
    </div>
  );
}
